import React, { useEffect, useState, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../components/ui/sheet";
import { Switch } from "../components/ui/switch";
import { Plus, Wifi, WifiOff, Activity, Cpu, Server, AlertCircle, Trash2, Settings, Plug, Search, ChevronRight, ChevronLeft, Check, X, PartyPopper, Copy, ScrollText, ChevronDown, FlaskConical, Archive, RotateCcw, ArrowLeftRight, Upload } from "lucide-react";
import { toast } from "sonner";
import { useSiteContext } from "../components/layout/Layout";
import CsvImportDialog from "../components/CsvImportDialog";

const GATEWAY_CSV_TEMPLATE = [
  "gateway_id,gateway_name,site_id,hardware_brand,hardware_model,primary_protocol,mqtt_broker_url,mqtt_topic_prefix,ip_address,port,poll_interval_seconds,firmware_version",
  "868221079916055,Phoenix Marketcity Mall Gateway,site_67c77a1a,custom_mqtt,WT-460M,mqtt,mqtt://fire.opticalc.in:8884,fire360pro/custom/868221079916055/data,,,10,SW:1.0.01",
];

const STATUS_TONE = {
  connected: { bg: "bg-emerald-500", text: "text-emerald-700", ring: "status-pulse-green", label: "CONNECTED" },
  error: { bg: "bg-red-500", text: "text-red-700", ring: "status-pulse-red", label: "ERROR" },
  disconnected: { bg: "bg-amber-500", text: "text-amber-700", ring: "", label: "DISCONNECTED" },
  unknown: { bg: "bg-slate-500", text: "text-slate-700", ring: "", label: "UNKNOWN" },
};

const PROTOCOL_LABELS = {
  bacnet_ip: "BACnet IP", bacnet_mstp: "BACnet MS/TP",
  modbus_tcp: "Modbus TCP", modbus_rtu: "Modbus RTU",
  mqtt: "MQTT", rest_api: "REST API", custom: "Custom",
};


function fmtAgo(seconds) {
  if (seconds < 60) return `${seconds}s`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h`;
  return `${Math.floor(seconds / 86400)}d`;
}

// Mirrors backend/device_adapters/generic_mqtt.py's _resolve_json_path — dot keys with
// optional trailing [N] indices, fully defensive (undefined instead of throwing).
function resolveJsonPath(payload, path) {
  let cur = payload;
  for (const rawPart of path.split(".")) {
    const part = rawPart.trim();
    if (!part) return undefined;
    const m = part.match(/^([^[\]]+)((?:\[\d+\])*)$/);
    if (!m) return undefined;
    const [, key, idxPart] = m;
    if (typeof cur !== "object" || cur === null || Array.isArray(cur) || !(key in cur)) return undefined;
    cur = cur[key];
    for (const idxMatch of idxPart.matchAll(/\[(\d+)\]/g)) {
      const i = Number(idxMatch[1]);
      if (!Array.isArray(cur) || i >= cur.length) return undefined;
      cur = cur[i];
    }
  }
  return cur;
}

function JsonPathFeedback({ parsedSample, path }) {
  if (!path) return null;
  if (!parsedSample) {
    return <div className="text-[11px] text-gray-500 mt-1">No sample payload to validate against yet.</div>;
  }
  const value = resolveJsonPath(parsedSample, path);
  if (value === undefined) {
    return <div className="text-[11px] text-red-700 mt-1">No match in sample payload.</div>;
  }
  const display = typeof value === "object" ? JSON.stringify(value) : String(value);
  return <div className="text-[11px] text-emerald-600 mt-1 font-mono">Matches: {display}</div>;
}

const EVENT_TONE = {
  connected: { dot: "bg-emerald-500", text: "text-emerald-700" },
  disconnected: { dot: "bg-amber-500", text: "text-amber-700" },
  parse_error: { dot: "bg-red-500", text: "text-red-700" },
};

function KpiCard({ label, value, icon: Icon, tone = "blue", testid, onClick }) {
  const grad = { blue: "from-sky-400 to-indigo-500", green: "from-emerald-400 to-emerald-600", red: "from-red-500 to-pink-600", orange: "from-orange-500 to-pink-500" };
  return (
    <div className={`glass p-5 rounded-lg ${onClick ? "cursor-pointer hover:bg-gray-50 transition" : ""}`} data-testid={testid} onClick={onClick}>
      <div className="flex items-start justify-between">
        <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${grad[tone]} flex items-center justify-center`}><Icon className="w-5 h-5 text-gray-900" /></div>
      </div>
      <div className="mt-4">
        <div className="text-3xl font-extrabold">{value}</div>
        <div className="text-xs uppercase tracking-wider text-gray-500 mt-1">{label}</div>
      </div>
    </div>
  );
}

export default function HardwareGateways() {
  const { sites } = useSiteContext();
  const [searchParams, setSearchParams] = useSearchParams();
  const [showOnboardBanner, setShowOnboardBanner] = useState(searchParams.get("onboarded") === "1");
  const [gateways, setGateways] = useState([]);
  const [stuckSummary, setStuckSummary] = useState({ count: 0, items: [] });
  const [stuckPopoverOpen, setStuckPopoverOpen] = useState(false);
  const [adapters, setAdapters] = useState({});
  const [filter, setFilter] = useState({ site: "all", brand: "all", protocol: "all", status: "all", q: "" });
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [step, setStep] = useState(1);
  const [form, setForm] = useState(initialForm());
  const [ioDrawer, setIoDrawer] = useState(null);
  const [ioRows, setIoRows] = useState([]);
  const [sample, setSample] = useState(null);
  const parsedSample = useMemo(() => {
    if (!sample?.last_raw_sample) return null;
    try { return JSON.parse(sample.last_raw_sample); } catch { return null; }
  }, [sample]);
  const [mapForm, setMapForm] = useState({ io_point_name: "", json_path: "", io_point_address: "", signal_type: "analog_input", engineering_unit: "", readback_io_point_name: "" });
  const [editingMappingId, setEditingMappingId] = useState(null);
  const [editMapForm, setEditMapForm] = useState({});
  const [slaveRows, setSlaveRows] = useState([]);
  const [slaveForm, setSlaveForm] = useState({ slave_id: "", label: "" });
  const [logDrawer, setLogDrawer] = useState(null);
  const [logData, setLogData] = useState({ events: [], recent_messages: [], register_health: [] });
  const [expandedMsg, setExpandedMsg] = useState(null);
  const [simDrawer, setSimDrawer] = useState(null);
  const [simPayloadText, setSimPayloadText] = useState("");
  const [simResult, setSimResult] = useState(null);
  const [simSending, setSimSending] = useState(false);
  const [showInactive, setShowInactive] = useState(false);
  const [replaceDrawer, setReplaceDrawer] = useState(null);
  const [newSerial, setNewSerial] = useState("");
  const [replacing, setReplacing] = useState(false);
  const [csvOpen, setCsvOpen] = useState(false);

  function initialForm() {
    return {
      gateway_id: "", gateway_name: "", site_id: "", hardware_brand: "easyio_fw", hardware_model: "FW-28",
      primary_protocol: "bacnet_ip", secondary_protocol: "", ip_address: "", port: 47808,
      bacnet_device_id: 1001, modbus_unit_id: 1, mqtt_broker_url: "", mqtt_topic_prefix: "",
      api_endpoint: "", poll_interval_seconds: 30, firmware_version: "", notes: "",
    };
  }

  const load = () => api.get("/hardware/gateways", { params: { include_inactive: showInactive } }).then((r) => setGateways(r.data));
  const loadStuckSummary = () => api.get("/hardware/gateways/stuck-registers-summary").then((r) => setStuckSummary(r.data)).catch(() => {});
  useEffect(() => { load(); loadStuckSummary(); api.get("/hardware/protocol-adapters").then((r) => setAdapters(r.data)); }, [showInactive]);

  const brandLabel = (key) => adapters[key]?.label || key;

  // Auto-fill defaults when brand changes
  useEffect(() => {
    if (!form.hardware_brand || !adapters[form.hardware_brand]) return;
    const a = adapters[form.hardware_brand];
    setForm((f) => ({ ...f, primary_protocol: a.primary_protocol || f.primary_protocol, port: a.default_port || f.port }));
  }, [form.hardware_brand, adapters]);

  // Generic MQTT devices must publish to a fixed, predictable topic the backend always subscribes to
  useEffect(() => {
    if (form.hardware_brand !== "custom_mqtt" || !form.gateway_id) return;
    const autoTopic = `fire360pro/custom/${form.gateway_id}/data`;
    setForm((f) => (f.mqtt_topic_prefix === autoTopic ? f : { ...f, mqtt_topic_prefix: autoTopic }));
  }, [form.hardware_brand, form.gateway_id]);

  const filtered = useMemo(() => gateways.filter((g) => {
    if (filter.site !== "all" && g.site_id !== filter.site) return false;
    if (filter.brand !== "all" && g.hardware_brand !== filter.brand) return false;
    if (filter.protocol !== "all" && g.primary_protocol !== filter.protocol) return false;
    if (filter.status !== "all" && g.connection_status !== filter.status) return false;
    if (filter.q && !`${g.gateway_name} ${g.ip_address || ""}`.toLowerCase().includes(filter.q.toLowerCase())) return false;
    return true;
  }), [gateways, filter]);

  const kpis = useMemo(() => {
    const total = gateways.length;
    const online = gateways.filter((g) => g.connection_status === "connected").length;
    const issues = gateways.filter((g) => g.connection_status === "error" || g.connection_status === "disconnected").length;
    const io = gateways.reduce((s, g) => s + (g.total_io_points || 0), 0);
    return { total, online, issues, io };
  }, [gateways]);

  const save = async () => {
    if (!form.gateway_name || !form.site_id) { toast.error("Name and site are required"); return; }
    if (!editing && !form.gateway_id) { toast.error("Serial Number / IMEI is required"); return; }
    try {
      if (editing) await api.patch(`/hardware/gateways/${editing.gateway_id}`, form);
      else await api.post("/hardware/gateways", form);
      toast.success(editing ? "Gateway updated" : "Gateway added");
      setOpen(false); setEditing(null); setForm(initialForm()); setStep(1);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const remove = async (g) => {
    const expected = sites.find((s) => s.site_id === g.site_id)?.name || "";
    const typed = window.prompt(`Type the site name "${expected}" to confirm deletion:`);
    if (typed !== expected) { toast.error("Name did not match — cancelled"); return; }
    await api.delete(`/hardware/gateways/${g.gateway_id}`);
    toast.success("Deleted"); load();
  };

  const decommission = async (g) => {
    if (!window.confirm(`Decommission "${g.gateway_name}"? Its IO mappings and history are kept — you can reactivate it anytime.`)) return;
    await api.post(`/hardware/gateways/${g.gateway_id}/decommission`);
    toast.success("Gateway decommissioned"); load();
  };

  const reactivate = async (g) => {
    await api.post(`/hardware/gateways/${g.gateway_id}/reactivate`);
    toast.success("Gateway reactivated"); load();
  };

  const openReplace = (g) => { setReplaceDrawer(g); setNewSerial(""); };
  const sendReplace = async () => {
    if (!newSerial.trim()) { toast.error("Enter the new device's serial / IMEI"); return; }
    setReplacing(true);
    try {
      await api.post(`/hardware/gateways/${replaceDrawer.gateway_id}/replace`, { new_gateway_id: newSerial.trim() });
      toast.success("Hardware replaced — IO mappings carried over to the new device");
      setReplaceDrawer(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Replace failed");
    } finally {
      setReplacing(false);
    }
  };

  const test = async (g) => {
    toast.message("Testing connection…", {
      description: g.primary_protocol === "mqtt" ? "Checking MQTT broker for an active client" : "Attempting TCP connection — protocol handshake not verified",
    });
    try {
      const r = await api.post(`/hardware/gateways/${g.gateway_id}/test`);
      const caveat = r.data.check_type === "tcp_reachability" ? " (TCP reachability only)" : "";
      if (r.data.success) toast.success(r.data.message + caveat, { description: `${r.data.latency_ms} ms` });
      else toast.error(r.data.message || "Connection failed");
      load();
    } catch { toast.error("Test failed — backend unreachable"); }
  };

  const validateStep2 = () => {
    if (form.primary_protocol === "modbus_tcp" || form.primary_protocol === "modbus_rtu") {
      const id = Number(form.modbus_unit_id);
      if (!id || id < 1 || id > 247) { toast.error("Modbus Unit ID must be between 1 and 247"); return false; }
    }
    if (form.primary_protocol === "mqtt") {
      if (!form.mqtt_broker_url) { toast.error("MQTT Broker URL is required"); return false; }
      if (!form.mqtt_topic_prefix) { toast.error("MQTT Topic Prefix is required"); return false; }
    }
    return true;
  };

  const goNext = () => {
    if (step === 2 && !validateStep2()) return;
    setStep(step + 1);
  };

  const openEdit = (g) => { setEditing(g); setForm({ ...initialForm(), ...g }); setStep(1); setOpen(true); };
  const openIo = async (g) => {
    setIoDrawer(g);
    setMapForm({ io_point_name: "", json_path: "", io_point_address: "", signal_type: "analog_input", engineering_unit: "" });
    setSlaveForm({ slave_id: "", label: "" });
    const r = await api.get(`/hardware/gateways/${g.gateway_id}/io-mappings`);
    setIoRows(r.data);
    const sl = await api.get(`/hardware/gateways/${g.gateway_id}/slaves`);
    setSlaveRows(sl.data);
    if (g.hardware_brand === "custom_mqtt") {
      const s = await api.get(`/hardware/gateways/${g.gateway_id}/sample-payload`);
      setSample(s.data);
    } else {
      setSample(null);
    }
  };
  const deleteIo = async (m) => {
    await api.delete(`/hardware/io-mappings/${m.mapping_id}`);
    toast.success("IO mapping removed");
    const r = await api.get(`/hardware/gateways/${ioDrawer.gateway_id}/io-mappings`);
    setIoRows(r.data); load();
  };
  const startEditMapping = (m) => {
    setEditingMappingId(m.mapping_id);
    setEditMapForm({ ...m });
  };
  const cancelEditMapping = () => {
    setEditingMappingId(null);
    setEditMapForm({});
  };
  const updateMapping = async (mappingId) => {
    try {
      await api.patch(`/hardware/io-mappings/${mappingId}`, editMapForm);
      toast.success("Mapping updated");
      setEditingMappingId(null);
      const r = await api.get(`/hardware/gateways/${ioDrawer.gateway_id}/io-mappings`);
      setIoRows(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update mapping");
    }
  };
  const addSlave = async () => {
    if (!slaveForm.slave_id) { toast.error("Slave ID is required"); return; }
    try {
      await api.post(`/hardware/gateways/${ioDrawer.gateway_id}/slaves`, slaveForm);
      toast.success("Slave ID registered");
      setSlaveForm({ slave_id: "", label: "" });
      const r = await api.get(`/hardware/gateways/${ioDrawer.gateway_id}/slaves`);
      setSlaveRows(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to register slave ID");
    }
  };
  const deleteSlave = async (slaveId) => {
    await api.delete(`/hardware/gateways/${ioDrawer.gateway_id}/slaves/${slaveId}`);
    toast.success("Slave ID removed");
    const r = await api.get(`/hardware/gateways/${ioDrawer.gateway_id}/slaves`);
    setSlaveRows(r.data);
  };
  const openLog = async (g) => {
    setLogDrawer(g);
    setExpandedMsg(null);
    const r = await api.get(`/hardware/gateways/${g.gateway_id}/connection-log`);
    setLogData(r.data);
  };

  const canSimulate = (g) => g.hardware_brand === "custom_mqtt" || (g.hardware_model || "").toUpperCase() === "WT-460M";
  const openSimulate = (g) => { setSimDrawer(g); setSimPayloadText(""); setSimResult(null); };
  const sendSimulation = async () => {
    setSimSending(true);
    setSimResult(null);
    try {
      let payload;
      if (simPayloadText.trim()) {
        try { payload = JSON.parse(simPayloadText); }
        catch { toast.error("Payload must be valid JSON"); setSimSending(false); return; }
      }
      const r = await api.post(`/hardware/gateways/${simDrawer.gateway_id}/simulate`, payload ? { payload } : {});
      setSimResult(r.data);
      if (r.data.success) toast.success("Test payload ingested successfully");
      else toast.error(r.data.message || r.data.error || "Simulation did not produce data");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Simulation failed");
    } finally {
      setSimSending(false);
    }
  };

  const addMapping = async () => {
    const isCustomMqtt = ioDrawer.hardware_brand === "custom_mqtt";
    const addressField = isCustomMqtt ? mapForm.json_path : mapForm.io_point_address;
    if (!mapForm.io_point_name || !addressField) {
      toast.error(`IO Point Name and ${isCustomMqtt ? "JSON Path" : "Register Address / Tag Name"} are required`);
      return;
    }
    try {
      await api.post(`/hardware/gateways/${ioDrawer.gateway_id}/io-mappings`, {
        gateway_id: ioDrawer.gateway_id, device_id: ioDrawer.gateway_id,
        io_point_name: mapForm.io_point_name,
        json_path: isCustomMqtt ? mapForm.json_path : undefined,
        io_point_address: isCustomMqtt ? undefined : mapForm.io_point_address,
        signal_type: mapForm.signal_type, engineering_unit: mapForm.engineering_unit || undefined,
        readback_io_point_name: mapForm.readback_io_point_name || undefined,
      });
      toast.success("Mapping added");
      setMapForm({ io_point_name: "", json_path: "", io_point_address: "", signal_type: "analog_input", engineering_unit: "", readback_io_point_name: "" });
      const r = await api.get(`/hardware/gateways/${ioDrawer.gateway_id}/io-mappings`);
      setIoRows(r.data); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to add mapping"); }
  };

  return (
    <div className="space-y-6" data-testid="gateways-page">
      {showOnboardBanner && (
        <div className="flex items-start gap-3 bg-emerald-500/10 border border-emerald-500/30 rounded-lg px-4 py-3" data-testid="onboard-banner">
          <PartyPopper className="w-5 h-5 text-emerald-400 shrink-0 mt-0.5" />
          <div className="flex-1">
            <div className="font-semibold text-emerald-300 text-sm">Setup complete — your gateway is registered!</div>
            <div className="text-xs text-gray-400 mt-0.5">
              Default IO mappings for your WT-460M have been pre-filled. Power on the device and live data will appear on the dashboard within seconds.
              You can add or edit mappings below.
            </div>
          </div>
          <button onClick={() => { setShowOnboardBanner(false); setSearchParams({}); }} className="text-gray-500 hover:text-white ml-2 mt-0.5" aria-label="Dismiss">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs text-gray-500">Dashboard → Hardware Gateways</div>
          <h1 className="text-3xl md:text-4xl font-extrabold">Hardware Gateways</h1>
          <p className="text-sm text-gray-500 mt-1">Universal Hardware Abstraction Layer · BACnet / Modbus / MQTT / REST · brand-agnostic</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => setCsvOpen(true)} variant="outline" className="border-gray-200" data-testid="import-gateways-btn">
            <Upload className="w-4 h-4 mr-1" strokeWidth={2.5} /> Import CSV
          </Button>
          <Button onClick={() => { setEditing(null); setForm(initialForm()); setStep(1); setOpen(true); }} className="f360-gradient border-0" data-testid="add-gateway-btn">
            <Plus className="w-4 h-4 mr-1" strokeWidth={2.5} /> Add Gateway
          </Button>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <KpiCard testid="kpi-total" label="Total Gateways" value={kpis.total} icon={Server} tone="blue" />
        <KpiCard testid="kpi-online" label="Online" value={kpis.online} icon={Wifi} tone="green" />
        <KpiCard testid="kpi-issues" label="Offline / Error" value={kpis.issues} icon={WifiOff} tone={kpis.issues ? "red" : "green"} />
        <KpiCard testid="kpi-io" label="Mapped I/O Points" value={kpis.io} icon={Activity} tone="orange" />
        <KpiCard
          testid="kpi-stuck"
          label="Stuck Registers"
          value={stuckSummary.count}
          icon={AlertCircle}
          tone={stuckSummary.count ? "red" : "green"}
          onClick={() => setStuckPopoverOpen((o) => !o)}
        />
      </div>

      {stuckPopoverOpen && (
        <div className="glass rounded-lg p-4" data-testid="stuck-registers-list">
          {stuckSummary.items.length === 0 ? (
            <div className="text-gray-500 text-sm text-center py-3">No registers have been stuck for over an hour. 🎉</div>
          ) : (
            <div className="space-y-2">
              <div className="text-xs uppercase tracking-widest text-orange-600 font-bold">Stuck Registers (analog, unchanged &gt;1h)</div>
              {stuckSummary.items.map((it, i) => (
                <div
                  key={`${it.gateway_id}-${it.register_name}-${i}`}
                  className="flex items-center justify-between gap-3 p-2 rounded-lg bg-gray-50 hover:bg-gray-100 cursor-pointer transition"
                  onClick={() => { setStuckPopoverOpen(false); openLog({ gateway_id: it.gateway_id, gateway_name: it.gateway_name }); }}
                >
                  <div className="min-w-0">
                    <div className="text-sm font-medium truncate">{it.gateway_name || it.gateway_id}</div>
                    <div className="text-[11px] text-gray-500 font-mono">{it.register_name}</div>
                  </div>
                  <div className="text-[11px] text-red-700 font-semibold whitespace-nowrap">stuck {fmtAgo(it.seconds_since_change)}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Filters */}
      <div className="glass rounded-lg p-3 flex gap-2 flex-wrap items-center">
        <div className="relative flex-1 min-w-[180px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
          <Input value={filter.q} onChange={(e) => setFilter({ ...filter, q: e.target.value })} placeholder="Search name or IP" className="pl-9 bg-gray-50 border-gray-200" data-testid="gw-search" />
        </div>
        <Select value={filter.site} onValueChange={(v) => setFilter({ ...filter, site: v })}>
          <SelectTrigger className="w-44 bg-gray-50 border-gray-200"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-white border-gray-200 text-gray-900"><SelectItem value="all">All sites</SelectItem>{sites.map((s) => <SelectItem key={s.site_id} value={s.site_id}>{s.name}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={filter.brand} onValueChange={(v) => setFilter({ ...filter, brand: v })}>
          <SelectTrigger className="w-52 bg-gray-50 border-gray-200"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-white border-gray-200 text-gray-900"><SelectItem value="all">All brands</SelectItem>{Object.keys(adapters).map((k) => <SelectItem key={k} value={k}>{brandLabel(k)}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={filter.protocol} onValueChange={(v) => setFilter({ ...filter, protocol: v })}>
          <SelectTrigger className="w-40 bg-gray-50 border-gray-200"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-white border-gray-200 text-gray-900"><SelectItem value="all">All protocols</SelectItem>{Object.entries(PROTOCOL_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={filter.status} onValueChange={(v) => setFilter({ ...filter, status: v })}>
          <SelectTrigger className="w-36 bg-gray-50 border-gray-200"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-white border-gray-200 text-gray-900">
            <SelectItem value="all">All status</SelectItem>
            <SelectItem value="connected">Connected</SelectItem>
            <SelectItem value="error">Error</SelectItem>
            <SelectItem value="disconnected">Disconnected</SelectItem>
            <SelectItem value="unknown">Unknown</SelectItem>
          </SelectContent>
        </Select>
        <label className="flex items-center gap-2 text-xs text-gray-500 ml-2">
          <Switch checked={showInactive} onCheckedChange={setShowInactive} data-testid="show-inactive-toggle" />
          Show decommissioned
        </label>
      </div>

      {/* Gateway cards */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {filtered.map((g) => {
          const tone = STATUS_TONE[g.connection_status] || STATUS_TONE.unknown;
          const siteName = sites.find((s) => s.site_id === g.site_id)?.name || g.site_id;
          return (
            <div key={g.gateway_id} className={`glass rounded-lg p-5 hover:border-orange-500/30 transition ${g.is_active === false ? "opacity-60" : ""}`} data-testid={`gw-card-${g.gateway_id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-center gap-2">
                  <span className={`w-3 h-3 rounded-full ${tone.bg} ${tone.ring}`}></span>
                  <span className={`text-[10px] font-bold uppercase tracking-wider ${tone.text}`}>{tone.label}</span>
                </div>
                <div className="flex items-center gap-1.5">
                  {g.is_active === false && <Badge className="bg-slate-50 text-slate-700 border-slate-200 text-[10px]">DECOMMISSIONED</Badge>}
                  <Badge className="border border-gray-200 bg-gray-50 text-[10px]">{brandLabel(g.hardware_brand)}</Badge>
                </div>
              </div>
              <h3 className="text-lg font-bold mt-3">{g.gateway_name}</h3>
              <div className="text-xs text-gray-500">{siteName} · {g.hardware_model || "Unspecified model"}</div>
              <div className="text-[11px] text-gray-400 font-mono mt-0.5">IMEI: {g.gateway_id}</div>

              <div className="grid grid-cols-3 gap-2 mt-4 text-xs">
                <div className="bg-gray-50 rounded p-2"><div className="text-gray-500 text-[10px] uppercase">Protocol</div><div className="font-bold text-orange-600">{PROTOCOL_LABELS[g.primary_protocol]}</div></div>
                <div className="bg-gray-50 rounded p-2"><div className="text-gray-500 text-[10px] uppercase">{g.primary_protocol === "mqtt" ? "Broker" : "IP"}</div><div className="font-mono text-[11px]">{g.primary_protocol === "mqtt" ? (g.mqtt_broker_url || "—").replace(/^mqtts?:\/\//, "").slice(0, 24) : (g.ip_address || "—")}</div></div>
                <div className="bg-gray-50 rounded p-2"><div className="text-gray-500 text-[10px] uppercase">Port</div><div className="font-mono">{g.port}</div></div>
                <div className="bg-gray-50 rounded p-2"><div className="text-gray-500 text-[10px] uppercase">I/O Points</div><div className="font-bold">{g.io_mappings_count || 0}</div></div>
                <div className="bg-gray-50 rounded p-2"><div className="text-gray-500 text-[10px] uppercase">Poll</div><div className="font-bold">{g.poll_interval_seconds}s</div></div>
                <div className="bg-gray-50 rounded p-2"><div className="text-gray-500 text-[10px] uppercase">Firmware</div><div className="font-mono text-[11px]">{g.firmware_version || "—"}</div></div>
              </div>

              <div className="text-[11px] text-gray-500 mt-3">Last heartbeat: {g.last_heartbeat ? new Date(g.last_heartbeat).toLocaleString() : "never"}</div>
              {g.primary_protocol !== "mqtt" && (
                <div className="flex items-start gap-1.5 mt-2 text-[11px] text-amber-600">
                  <AlertCircle className="w-3.5 h-3.5 shrink-0 mt-0.5" strokeWidth={2.5} />
                  No data ingestion support yet for {PROTOCOL_LABELS[g.primary_protocol]} — this gateway will not produce telemetry.
                </div>
              )}

              <div className="flex gap-2 mt-4 flex-wrap">
                <Button size="sm" onClick={() => openIo(g)} className="bg-gray-100 hover:bg-gray-200 text-gray-900" data-testid={`io-btn-${g.gateway_id}`}><Plug className="w-3.5 h-3.5 mr-1" strokeWidth={2.5} /> IO Mappings</Button>
                <Button size="sm" onClick={() => openLog(g)} className="bg-gray-100 hover:bg-gray-200 text-gray-900" data-testid={`log-btn-${g.gateway_id}`}><ScrollText className="w-3.5 h-3.5 mr-1" strokeWidth={2.5} /> Connection Log</Button>
                {canSimulate(g) && (
                  <Button size="sm" onClick={() => openSimulate(g)} className="bg-sky-50 border border-sky-200 text-sky-700 hover:bg-sky-500/30" data-testid={`sim-btn-${g.gateway_id}`}><FlaskConical className="w-3.5 h-3.5 mr-1" strokeWidth={2.5} /> Simulate</Button>
                )}
                <Button size="sm" onClick={() => openEdit(g)} className="bg-gray-100 hover:bg-gray-200 text-gray-900" data-testid={`edit-btn-${g.gateway_id}`}><Settings className="w-3.5 h-3.5 mr-1" strokeWidth={2.5} /> Edit</Button>
                <Button size="sm" onClick={() => test(g)} className="bg-emerald-50 border border-emerald-200 text-emerald-700 hover:bg-emerald-500/30" data-testid={`test-btn-${g.gateway_id}`}><Activity className="w-3.5 h-3.5 mr-1" strokeWidth={2.5} /> Test</Button>
                {g.is_active === false ? (
                  <Button size="sm" onClick={() => reactivate(g)} className="bg-emerald-50 border border-emerald-200 text-emerald-700 hover:bg-emerald-500/30" data-testid={`reactivate-btn-${g.gateway_id}`}><RotateCcw className="w-3.5 h-3.5 mr-1" strokeWidth={2.5} /> Reactivate</Button>
                ) : (
                  <>
                    <Button size="sm" onClick={() => openReplace(g)} className="bg-gray-100 hover:bg-gray-200 text-gray-900" data-testid={`replace-btn-${g.gateway_id}`}><ArrowLeftRight className="w-3.5 h-3.5 mr-1" strokeWidth={2.5} /> Replace Hardware</Button>
                    <Button size="sm" onClick={() => decommission(g)} className="bg-amber-50 border border-amber-200 text-amber-700 hover:bg-amber-500/30" data-testid={`decom-btn-${g.gateway_id}`}><Archive className="w-3.5 h-3.5 mr-1" strokeWidth={2.5} /> Decommission</Button>
                  </>
                )}
                <Button size="sm" onClick={() => remove(g)} className="bg-red-50 border border-red-200 text-red-700 hover:bg-red-500/30 ml-auto" data-testid={`del-btn-${g.gateway_id}`}><Trash2 className="w-3.5 h-3.5" strokeWidth={2.5} /></Button>
              </div>
            </div>
          );
        })}
        {!filtered.length && <div className="glass rounded-lg p-10 text-center text-gray-500 col-span-full">No gateways match your filters.</div>}
      </div>

      {/* Add/Edit wizard */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white border-gray-200 text-gray-900 max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {editing ? "Edit Hardware Gateway" : "Add Hardware Gateway"}
              <span className="text-xs text-gray-500 ml-auto">Step {step} of 3</span>
            </DialogTitle>
          </DialogHeader>
          <div className="flex gap-2 mb-4">
            {[1, 2, 3].map((n) => (
              <div key={n} className={`flex-1 h-1.5 rounded ${step >= n ? "f360-gradient" : "bg-white/10"}`} />
            ))}
          </div>

          {step === 1 && (
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2"><Label>Gateway Name *</Label><Input value={form.gateway_name} onChange={(e) => setForm({ ...form, gateway_name: e.target.value })} className="bg-gray-50 border-gray-200 mt-1" data-testid="gw-name" /></div>
              {!editing && (
                <div className="col-span-2">
                  <Label>Serial Number / IMEI *</Label>
                  <Input value={form.gateway_id} onChange={(e) => setForm({ ...form, gateway_id: e.target.value.trim() })} className="bg-gray-50 border-gray-200 mt-1 font-mono" placeholder="e.g. 868221079916055" data-testid="gw-serial" />
                  <div className="text-[11px] text-amber-600 mt-1">Must match the device IMEI exactly — this is used to match incoming MQTT messages.</div>
                </div>
              )}
              <div className="col-span-2"><Label>Site *</Label>
                <Select value={form.site_id} onValueChange={(v) => setForm({ ...form, site_id: v })}>
                  <SelectTrigger className="bg-gray-50 border-gray-200 mt-1" data-testid="gw-site"><SelectValue placeholder="Select site" /></SelectTrigger>
                  <SelectContent className="bg-white border-gray-200 text-gray-900">{sites.map((s) => <SelectItem key={s.site_id} value={s.site_id}>{s.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="col-span-2"><Label>Hardware Brand *</Label>
                <Select value={form.hardware_brand} onValueChange={(v) => setForm({ ...form, hardware_brand: v })}>
                  <SelectTrigger className="bg-gray-50 border-gray-200 mt-1" data-testid="gw-brand"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white border-gray-200 text-gray-900">{Object.keys(adapters).map((k) => <SelectItem key={k} value={k}>{brandLabel(k)}</SelectItem>)}</SelectContent>
                </Select>
                {adapters[form.hardware_brand]?.notes && <div className="text-[11px] text-amber-300/80 mt-1 italic">{adapters[form.hardware_brand].notes}</div>}
              </div>
              <div><Label>Hardware Model</Label><Input value={form.hardware_model || ""} onChange={(e) => setForm({ ...form, hardware_model: e.target.value })} className="bg-gray-50 border-gray-200 mt-1" /></div>
              <div><Label>Firmware Version</Label><Input value={form.firmware_version || ""} onChange={(e) => setForm({ ...form, firmware_version: e.target.value })} className="bg-gray-50 border-gray-200 mt-1" placeholder="v3.2.1" /></div>
            </div>
          )}

          {step === 2 && (
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Primary Protocol *</Label>
                <Select value={form.primary_protocol} onValueChange={(v) => setForm({ ...form, primary_protocol: v })}>
                  <SelectTrigger className="bg-gray-50 border-gray-200 mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white border-gray-200 text-gray-900">{Object.entries(PROTOCOL_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}{k !== "mqtt" ? " (no data ingestion yet)" : ""}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Secondary Protocol</Label>
                <Select value={form.secondary_protocol || "_none"} onValueChange={(v) => setForm({ ...form, secondary_protocol: v === "_none" ? "" : v })}>
                  <SelectTrigger className="bg-gray-50 border-gray-200 mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white border-gray-200 text-gray-900"><SelectItem value="_none">None</SelectItem>{Object.entries(PROTOCOL_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}{k !== "mqtt" ? " (no data ingestion yet)" : ""}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              {form.primary_protocol !== "mqtt" && (
                <div className="col-span-2 flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-700">
                  <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" strokeWidth={2.5} />
                  <div>
                    Data ingestion for <strong>{PROTOCOL_LABELS[form.primary_protocol]}</strong> is not yet implemented. Only MQTT
                    devices currently send live data to Fire360Pro — this gateway can be registered and IP/port tested for
                    reachability, but it will not produce telemetry until {PROTOCOL_LABELS[form.primary_protocol]} support is added.
                  </div>
                </div>
              )}
              {(form.primary_protocol === "bacnet_ip" || form.primary_protocol === "modbus_tcp" || form.primary_protocol === "rest_api") && (<>
                <div><Label>IP Address</Label><Input value={form.ip_address || ""} onChange={(e) => setForm({ ...form, ip_address: e.target.value })} className="bg-gray-50 border-gray-200 mt-1" placeholder="192.168.10.5" /></div>
                <div><Label>Port</Label><Input type="number" value={form.port} onChange={(e) => setForm({ ...form, port: Number(e.target.value) })} className="bg-gray-50 border-gray-200 mt-1" /></div>
              </>)}
              {form.primary_protocol === "bacnet_ip" && (
                <div><Label>BACnet Device ID</Label><Input type="number" value={form.bacnet_device_id || ""} onChange={(e) => setForm({ ...form, bacnet_device_id: Number(e.target.value) })} className="bg-gray-50 border-gray-200 mt-1" /></div>
              )}
              {(form.primary_protocol === "modbus_tcp" || form.primary_protocol === "modbus_rtu") && (
                <div>
                  <Label>Modbus Unit ID (1–247) *</Label>
                  <Input type="number" min="1" max="247" value={form.modbus_unit_id || 1} onChange={(e) => setForm({ ...form, modbus_unit_id: Number(e.target.value) })} className="bg-gray-50 border-gray-200 mt-1" />
                  {form.primary_protocol === "modbus_rtu" && <div className="text-[11px] text-amber-600 mt-1">RTU requires RS-485 wiring — confirm baud rate, parity, and stop bits with the field engineer.</div>}
                </div>
              )}
              {form.primary_protocol === "mqtt" && (<>
                <div className="col-span-2"><Label>MQTT Broker URL *</Label><Input value={form.mqtt_broker_url || ""} onChange={(e) => setForm({ ...form, mqtt_broker_url: e.target.value })} className="bg-gray-50 border-gray-200 mt-1" placeholder="mqtt://fire.opticalc.in:8884" /></div>
                <div className="col-span-2">
                  <Label>Topic Prefix *</Label>
                  {form.hardware_brand === "custom_mqtt" ? (
                    <div className="flex gap-2 mt-1">
                      <Input value={form.mqtt_topic_prefix || ""} readOnly className="bg-gray-100 border-gray-200 mt-0 font-mono text-xs flex-1" />
                      <Button type="button" size="sm" variant="outline" className="border-gray-200 shrink-0" onClick={() => { navigator.clipboard.writeText(form.mqtt_topic_prefix || ""); toast.success("Topic copied"); }}><Copy className="w-3.5 h-3.5" /></Button>
                    </div>
                  ) : (
                    <Input value={form.mqtt_topic_prefix || ""} onChange={(e) => setForm({ ...form, mqtt_topic_prefix: e.target.value })} className="bg-gray-50 border-gray-200 mt-1" placeholder="iot1/{IMEI}/" />
                  )}
                </div>
                <div className="col-span-2 text-[11px] text-amber-600 -mt-1">
                  {form.hardware_brand === "custom_mqtt"
                    ? "Configure your device to publish JSON to this exact topic. After saving, define field mappings in the IO Mappings drawer below — no code required."
                    : <>The device must be configured with this exact broker URL and a publish topic of <span className="font-mono">{`iot1/${form.gateway_id || "{IMEI}"}/event/`}</span> on its own config tool.</>}
                </div>
              </>)}
              {form.primary_protocol === "rest_api" && (
                <div className="col-span-2"><Label>API Endpoint</Label><Input value={form.api_endpoint || ""} onChange={(e) => setForm({ ...form, api_endpoint: e.target.value })} className="bg-gray-50 border-gray-200 mt-1" placeholder="https://api.gateway.local/v1" /></div>
              )}
            </div>
          )}

          {step === 3 && (
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Poll Interval (sec)</Label><Input type="number" min="5" value={form.poll_interval_seconds} onChange={(e) => setForm({ ...form, poll_interval_seconds: Number(e.target.value) })} className="bg-gray-50 border-gray-200 mt-1" /></div>
              <div className="col-span-2"><Label>Notes</Label><Input value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="bg-gray-50 border-gray-200 mt-1" placeholder="Field engineer notes, panel positioning, cable runs..." /></div>
              <div className="col-span-2 bg-gray-50 rounded-lg p-4 text-xs space-y-1.5">
                <div className="text-orange-300 font-bold uppercase tracking-wider text-[10px]">Review</div>
                <div><span className="text-gray-500">Name:</span> {form.gateway_name}</div>
                {!editing && <div><span className="text-gray-500">Serial / IMEI:</span> <span className="font-mono">{form.gateway_id || "—"}</span></div>}
                <div><span className="text-gray-500">Site:</span> {sites.find((s) => s.site_id === form.site_id)?.name}</div>
                <div><span className="text-gray-500">Brand / Model:</span> {brandLabel(form.hardware_brand)} · {form.hardware_model}</div>
                <div><span className="text-gray-500">Protocol:</span> {PROTOCOL_LABELS[form.primary_protocol]} {form.secondary_protocol ? `+ ${PROTOCOL_LABELS[form.secondary_protocol]}` : ""}</div>
                <div><span className="text-gray-500">Endpoint:</span> {form.primary_protocol === "mqtt" ? form.mqtt_broker_url : `${form.ip_address || "—"}:${form.port}`}</div>
              </div>
            </div>
          )}

          <DialogFooter>
            <div className="flex gap-2 w-full justify-between">
              <Button variant="ghost" onClick={() => step === 1 ? setOpen(false) : setStep(step - 1)}>
                {step === 1 ? "Cancel" : <><ChevronLeft className="w-4 h-4 mr-1" strokeWidth={2.5} /> Back</>}
              </Button>
              {step < 3 ? (
                <Button onClick={goNext} className="f360-gradient border-0" data-testid="gw-next-btn">Next <ChevronRight className="w-4 h-4 ml-1" strokeWidth={2.5} /></Button>
              ) : (
                <Button onClick={save} className="f360-gradient border-0" data-testid="gw-save-btn"><Check className="w-4 h-4 mr-1" strokeWidth={2.5} /> {editing ? "Update Gateway" : "Create Gateway"}</Button>
              )}
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* IO Mappings drawer */}
      <Sheet open={!!ioDrawer} onOpenChange={(o) => !o && setIoDrawer(null)}>
        <SheetContent side="right" className="bg-white border-gray-200 text-gray-900 w-full sm:max-w-2xl overflow-y-auto" data-testid="io-drawer">
          {ioDrawer && (
            <>
              <SheetHeader>
                <SheetTitle className="text-gray-900 text-xl">IO Mappings — {ioDrawer.gateway_name}</SheetTitle>
                <div className="text-xs text-gray-500">Maps hardware I/O points to Fire360Pro devices · {PROTOCOL_LABELS[ioDrawer.primary_protocol]}</div>
              </SheetHeader>
              <div className="mt-4 space-y-2">
                {ioRows.map((m) => (
                  editingMappingId === m.mapping_id ? (
                    <div key={m.mapping_id} className="bg-gray-50 rounded-lg p-3 space-y-2" data-testid={`io-row-edit-${m.mapping_id}`}>
                      <div className="grid grid-cols-2 gap-2">
                        <Input placeholder="IO Point Name" value={editMapForm.io_point_name || ""} onChange={(e) => setEditMapForm({ ...editMapForm, io_point_name: e.target.value })} className="bg-white border-gray-200" />
                        {m.json_path !== null && m.json_path !== undefined ? (
                          <div>
                            <Input placeholder="JSON Path" value={editMapForm.json_path || ""} onChange={(e) => setEditMapForm({ ...editMapForm, json_path: e.target.value })} className="bg-white border-gray-200 font-mono text-xs" />
                            <JsonPathFeedback parsedSample={parsedSample} path={editMapForm.json_path} />
                          </div>
                        ) : (
                          <Input placeholder="Register Address / Tag Name" maxLength={13} value={editMapForm.io_point_address || ""} onChange={(e) => setEditMapForm({ ...editMapForm, io_point_address: e.target.value })} className="bg-white border-gray-200 font-mono text-xs" />
                        )}
                        <Select value={editMapForm.signal_type} onValueChange={(v) => setEditMapForm({ ...editMapForm, signal_type: v })}>
                          <SelectTrigger className="bg-white border-gray-200"><SelectValue /></SelectTrigger>
                          <SelectContent className="bg-white border-gray-200 text-gray-900">
                            <SelectItem value="analog_input">Analog Input</SelectItem>
                            <SelectItem value="digital_input">Digital Input</SelectItem>
                            <SelectItem value="binary_input">Binary Input</SelectItem>
                            <SelectItem value="pulse_counter">Pulse Counter</SelectItem>
                            <SelectItem value="digital_output">Digital Output (writable)</SelectItem>
                            <SelectItem value="binary_output">Binary Output (writable)</SelectItem>
                            <SelectItem value="analog_output">Analog Output (writable)</SelectItem>
                          </SelectContent>
                        </Select>
                        <Input placeholder="Unit (e.g. bar, °C)" value={editMapForm.engineering_unit || ""} onChange={(e) => setEditMapForm({ ...editMapForm, engineering_unit: e.target.value })} className="bg-white border-gray-200" />
                      </div>
                      {["digital_output", "binary_output", "analog_output"].includes(editMapForm.signal_type) && (
                        <Input
                          placeholder="Readback parameter (optional — e.g. OUT1 reads back as output-1)"
                          value={editMapForm.readback_io_point_name || ""}
                          onChange={(e) => setEditMapForm({ ...editMapForm, readback_io_point_name: e.target.value })}
                          className="bg-white border-gray-200 font-mono text-xs"
                        />
                      )}
                      <div className="flex items-center gap-2 justify-end">
                        <Button size="sm" variant="ghost" onClick={cancelEditMapping} className="text-gray-500 h-7"><X className="w-3.5 h-3.5 mr-1" strokeWidth={2.5} /> Cancel</Button>
                        <Button size="sm" onClick={() => updateMapping(m.mapping_id)} className="f360-gradient border-0 h-7"><Check className="w-3.5 h-3.5 mr-1" strokeWidth={2.5} /> Save</Button>
                      </div>
                    </div>
                  ) : (
                    <div key={m.mapping_id} className="bg-gray-50 rounded-lg p-3 flex items-center gap-3" data-testid={`io-row-${m.mapping_id}`}>
                      <Cpu className="w-4 h-4 text-orange-400" strokeWidth={2.5} />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-bold truncate">{m.io_point_name || m.io_point_address}</div>
                        <div className="text-[11px] text-gray-500">
                          {m.json_path ? <>path <span className="font-mono text-orange-300">{m.json_path}</span></> : <>addr <span className="font-mono text-orange-300">{m.io_point_address}</span></>}
                          {" "}· {m.signal_type} · {m.engineering_min}–{m.engineering_max} {m.engineering_unit}
                          {m.readback_io_point_name && <> · reads back as <span className="font-mono text-orange-300">{m.readback_io_point_name}</span></>}
                        </div>
                      </div>
                      <Button size="sm" variant="ghost" onClick={() => startEditMapping(m)} className="text-gray-500 h-7" data-testid={`edit-io-${m.mapping_id}`}><Settings className="w-3.5 h-3.5" strokeWidth={2.5} /></Button>
                      <Button size="sm" variant="ghost" onClick={() => deleteIo(m)} className="text-red-400 h-7"><Trash2 className="w-3.5 h-3.5" strokeWidth={2.5} /></Button>
                    </div>
                  )
                ))}
                {!ioRows.length && <div className="text-gray-500 text-sm text-center py-6">No IO mappings yet. Add via the device page or upload a CSV.</div>}
              </div>

              <div className="mt-6 border-t border-gray-200 pt-4 space-y-3">
                <div className="text-xs uppercase tracking-widest text-orange-300 font-bold">Registered Slave IDs</div>
                <div className="text-[11px] text-gray-500">
                  Declares which Modbus Slave IDs are valid under this gateway. Once at least one
                  is registered, data from any other Slave ID is dropped and logged instead of stored.
                </div>
                <div className="space-y-2">
                  {slaveRows.map((s) => (
                    <div key={s.slave_id} className="bg-gray-50 rounded-lg p-3 flex items-center gap-3" data-testid={`slave-row-${s.slave_id}`}>
                      <Cpu className="w-4 h-4 text-orange-400" strokeWidth={2.5} />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-bold truncate">Slave {s.slave_id}{s.label ? ` — ${s.label}` : ""}</div>
                        <div className="text-[11px] text-gray-500">Registered {s.registered_at ? new Date(s.registered_at).toLocaleString() : "—"}</div>
                      </div>
                      <Button size="sm" variant="ghost" onClick={() => deleteSlave(s.slave_id)} className="text-red-400 h-7"><Trash2 className="w-3.5 h-3.5" strokeWidth={2.5} /></Button>
                    </div>
                  ))}
                  {!slaveRows.length && <div className="text-gray-500 text-sm text-center py-3">No slave IDs registered yet — all incoming slave IDs are currently accepted.</div>}
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <Input placeholder="Slave ID (e.g. 50)" value={slaveForm.slave_id} onChange={(e) => setSlaveForm({ ...slaveForm, slave_id: e.target.value })} className="bg-gray-50 border-gray-200 font-mono text-xs" />
                  <Input placeholder="Label (e.g. Fire Panel)" value={slaveForm.label} onChange={(e) => setSlaveForm({ ...slaveForm, label: e.target.value })} className="bg-gray-50 border-gray-200" />
                </div>
                <Button onClick={addSlave} className="f360-gradient border-0" size="sm"><Plus className="w-3.5 h-3.5 mr-1" strokeWidth={2.5} /> Register Slave ID</Button>
              </div>

              <div className="mt-6 border-t border-gray-200 pt-4 space-y-3">
                <div className="text-xs uppercase tracking-widest text-orange-300 font-bold">Add Mapping</div>
                <div className="grid grid-cols-2 gap-2">
                  <Input placeholder="IO Point Name (e.g. Pump Reset Relay)" value={mapForm.io_point_name} onChange={(e) => setMapForm({ ...mapForm, io_point_name: e.target.value })} className="bg-gray-50 border-gray-200" />
                  {ioDrawer.hardware_brand === "custom_mqtt" ? (
                    <div>
                      <Input placeholder="JSON Path (e.g. data.temp1)" value={mapForm.json_path} onChange={(e) => setMapForm({ ...mapForm, json_path: e.target.value })} className="bg-gray-50 border-gray-200 font-mono text-xs" />
                      <JsonPathFeedback parsedSample={parsedSample} path={mapForm.json_path} />
                    </div>
                  ) : (
                    <Input placeholder="Register Address / Tag Name (e.g. OUT2)" maxLength={13} value={mapForm.io_point_address} onChange={(e) => setMapForm({ ...mapForm, io_point_address: e.target.value })} className="bg-gray-50 border-gray-200 font-mono text-xs" />
                  )}
                  <Select value={mapForm.signal_type} onValueChange={(v) => setMapForm({ ...mapForm, signal_type: v })}>
                    <SelectTrigger className="bg-gray-50 border-gray-200"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-white border-gray-200 text-gray-900">
                      <SelectItem value="analog_input">Analog Input</SelectItem>
                      <SelectItem value="digital_input">Digital Input</SelectItem>
                      <SelectItem value="binary_input">Binary Input</SelectItem>
                      <SelectItem value="pulse_counter">Pulse Counter</SelectItem>
                      <SelectItem value="digital_output">Digital Output (writable)</SelectItem>
                      <SelectItem value="binary_output">Binary Output (writable)</SelectItem>
                      <SelectItem value="analog_output">Analog Output (writable)</SelectItem>
                    </SelectContent>
                  </Select>
                  <Input placeholder="Unit (e.g. bar, °C)" value={mapForm.engineering_unit} onChange={(e) => setMapForm({ ...mapForm, engineering_unit: e.target.value })} className="bg-gray-50 border-gray-200" />
                </div>
                {["digital_output", "binary_output", "analog_output"].includes(mapForm.signal_type) && (
                  <>
                    <div className="text-[11px] text-amber-600">Writable registers appear as remote controls on the Gateway Commands page immediately after saving.</div>
                    <Input
                      placeholder="Readback parameter (optional — only if the device reports this output back under a different name, e.g. write tag OUT1 reads back as output-1)"
                      value={mapForm.readback_io_point_name}
                      onChange={(e) => setMapForm({ ...mapForm, readback_io_point_name: e.target.value })}
                      className="bg-gray-50 border-gray-200 font-mono text-xs"
                    />
                  </>
                )}
                <Button onClick={addMapping} className="f360-gradient border-0" size="sm"><Plus className="w-3.5 h-3.5 mr-1" strokeWidth={2.5} /> Add Mapping</Button>

                {ioDrawer.hardware_brand === "custom_mqtt" && (
                  <>
                    <div className="text-[11px] text-gray-500 mt-2">Sample payload received from this device — use it to find the right JSON path:</div>
                    {sample?.last_raw_sample ? (
                      <pre className="bg-gray-50 border border-gray-200 rounded p-3 text-[11px] font-mono overflow-x-auto whitespace-pre-wrap">{sample.last_raw_sample}</pre>
                    ) : (
                      <div className="text-gray-500 text-xs">No payload received yet — power on the device and publish to <span className="font-mono">{`fire360pro/custom/${ioDrawer.gateway_id}/data`}</span>, then reopen this drawer.</div>
                    )}
                  </>
                )}
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>

      {/* Connection Log drawer */}
      <Sheet open={!!logDrawer} onOpenChange={(o) => !o && setLogDrawer(null)}>
        <SheetContent side="right" className="bg-white border-gray-200 text-gray-900 w-full sm:max-w-2xl overflow-y-auto" data-testid="log-drawer">
          {logDrawer && (
            <>
              <SheetHeader>
                <SheetTitle className="text-gray-900 text-xl">Connection Log — {logDrawer.gateway_name}</SheetTitle>
                <div className="text-xs text-gray-500">Real connect/disconnect/error events and recent telemetry — self-service diagnostics, no SSH required</div>
              </SheetHeader>

              <div className="mt-4">
                <div className="text-[10px] uppercase tracking-widest text-orange-300 font-bold mb-2">Event Timeline</div>
                <div className="space-y-1.5">
                  {logData.events.map((ev) => {
                    const tone = EVENT_TONE[ev.event_type] || EVENT_TONE.disconnected;
                    return (
                      <div key={ev.log_id} className="flex items-start gap-2 bg-gray-50 rounded p-2.5 text-xs" data-testid={`log-event-${ev.log_id}`}>
                        <span className={`w-2 h-2 rounded-full mt-1 shrink-0 ${tone.dot}`}></span>
                        <div className="flex-1 min-w-0">
                          <span className={`font-bold uppercase tracking-wider text-[10px] ${tone.text}`}>{ev.event_type.replace("_", " ")}</span>
                          <span className="text-gray-500 ml-2">{new Date(ev.created_at).toLocaleString()}</span>
                          {ev.detail && <div className="text-gray-600 mt-0.5">{ev.detail}</div>}
                        </div>
                      </div>
                    );
                  })}
                  {!logData.events.length && <div className="text-gray-500 text-sm text-center py-4">No connection events recorded yet.</div>}
                </div>
              </div>

              <div className="mt-6">
                <div className="text-[10px] uppercase tracking-widest text-orange-300 font-bold mb-2">Recent Messages</div>
                <div className="space-y-1.5">
                  {logData.recent_messages.map((m, i) => {
                    const key = `${m.ingested_at}_${i}`;
                    const open = expandedMsg === key;
                    return (
                      <div key={key} className="bg-gray-50 rounded text-xs" data-testid={`log-msg-${i}`}>
                        <button className="w-full flex items-center gap-2 p-2.5 text-left" onClick={() => setExpandedMsg(open ? null : key)}>
                          <ChevronDown className={`w-3.5 h-3.5 text-gray-500 transition-transform ${open ? "rotate-180" : ""}`} />
                          <span className="text-gray-500">{new Date(m.ingested_at).toLocaleString()}</span>
                          <span className="text-orange-300 font-mono">slave {m.slave_id}</span>
                          <span className="text-gray-500 ml-auto">{Object.keys(m.registers || {}).length} registers</span>
                        </button>
                        {open && (
                          <pre className="px-2.5 pb-2.5 font-mono text-[11px] text-gray-700 overflow-x-auto whitespace-pre-wrap">{JSON.stringify(m.registers, null, 2)}</pre>
                        )}
                      </div>
                    );
                  })}
                  {!logData.recent_messages.length && <div className="text-gray-500 text-sm text-center py-4">No messages received yet from this gateway.</div>}
                </div>
              </div>

              <div className="mt-6">
                <div className="text-[10px] uppercase tracking-widest text-orange-300 font-bold mb-2">Register Health</div>
                <div className="text-[11px] text-gray-500 mb-2">Analog registers that haven't changed value in over an hour are flagged — digital/binary registers staying constant is normal and never flagged.</div>
                <div className="space-y-1.5">
                  {logData.register_health.map((r) => (
                    <div key={r.register_name} className="flex items-center justify-between gap-2 bg-gray-50 rounded p-2.5 text-xs" data-testid={`reg-health-${r.register_name}`}>
                      <span className="font-mono text-gray-700">{r.register_name}</span>
                      <span className="text-gray-500">value: {r.last_value}</span>
                      <span className="text-gray-500">changed {fmtAgo(r.seconds_since_change)} ago</span>
                      {r.stuck && <Badge className="bg-amber-50 border border-amber-200 text-amber-700 text-[10px]">Possibly Stuck</Badge>}
                    </div>
                  ))}
                  {!logData.register_health.length && <div className="text-gray-500 text-sm text-center py-4">No register history yet.</div>}
                </div>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>

      {/* Replace Hardware dialog */}
      <Dialog open={!!replaceDrawer} onOpenChange={(o) => !o && setReplaceDrawer(null)}>
        <DialogContent className="bg-white border-gray-200 text-gray-900 max-w-md">
          <DialogHeader><DialogTitle>Replace Hardware — {replaceDrawer?.gateway_name}</DialogTitle></DialogHeader>
          <div className="space-y-3 text-sm">
            <p className="text-xs text-gray-500">
              Use this when the physical device is being swapped (e.g. failed hardware). All IO
              mappings carry over automatically to the new device. The old gateway record is kept
              for historical telemetry but marked decommissioned.
            </p>
            <div>
              <Label>New Device Serial Number / IMEI *</Label>
              <Input value={newSerial} onChange={(e) => setNewSerial(e.target.value.trim())} className="bg-gray-50 border-gray-200 mt-1 font-mono" placeholder="e.g. 868221079916099" data-testid="replace-serial-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReplaceDrawer(null)} className="border-gray-200">Cancel</Button>
            <Button onClick={sendReplace} disabled={replacing} className="f360-gradient border-0" data-testid="replace-confirm-btn">
              {replacing ? "Replacing…" : "Replace Hardware"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <CsvImportDialog
        open={csvOpen}
        onOpenChange={setCsvOpen}
        title="Import Gateways from CSV"
        endpoint="/hardware/gateways/bulk-import"
        templateRows={GATEWAY_CSV_TEMPLATE}
        templateFilename="gateways_template.csv"
        onDone={load}
      />

      {/* Simulate payload dialog */}
      <Dialog open={!!simDrawer} onOpenChange={(o) => !o && setSimDrawer(null)}>
        <DialogContent className="bg-white border-gray-200 text-gray-900 max-w-xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Send Test Payload — {simDrawer?.gateway_name}</DialogTitle></DialogHeader>
          <div className="space-y-3 text-sm">
            <p className="text-xs text-gray-500">
              Publishes a message through the real MQTT pipeline — exactly what a physical device would
              trigger. Leave blank to auto-generate from this gateway's configured IO mappings, or paste
              a custom JSON payload to test an exact shape before connecting hardware.
            </p>
            <textarea
              value={simPayloadText}
              onChange={(e) => setSimPayloadText(e.target.value)}
              placeholder={'Leave blank to auto-generate, or paste custom JSON e.g. {"data": {"temp1": 23.5}}'}
              className="w-full h-28 bg-gray-50 border border-gray-200 rounded px-3 py-2 font-mono text-xs"
              data-testid="sim-payload-input"
            />
            <Button onClick={sendSimulation} disabled={simSending} className="f360-gradient border-0" size="sm" data-testid="sim-send-btn">
              {simSending ? "Sending…" : <><FlaskConical className="w-3.5 h-3.5 mr-1" strokeWidth={2.5} /> Send Test Payload</>}
            </Button>

            {simResult && (
              <div className="border-t border-gray-200 pt-3 space-y-2">
                <div className={`text-xs font-bold uppercase tracking-wider ${simResult.success ? "text-emerald-600" : "text-red-600"}`}>
                  {simResult.success ? "Ingested successfully" : "Not ingested"}
                </div>
                {!simResult.success && (
                  <div className="text-xs text-gray-600">{simResult.message || simResult.error}</div>
                )}
                {simResult.success && (
                  <pre className="bg-gray-50 border border-gray-200 rounded p-3 text-[11px] font-mono overflow-x-auto whitespace-pre-wrap">{JSON.stringify(simResult.registers, null, 2)}</pre>
                )}
                {simResult.payload_sent && (
                  <>
                    <div className="text-[10px] uppercase tracking-widest text-gray-500 font-bold mt-2">Payload Sent</div>
                    <pre className="bg-gray-50 border border-gray-200 rounded p-3 text-[11px] font-mono overflow-x-auto whitespace-pre-wrap">{JSON.stringify(simResult.payload_sent, null, 2)}</pre>
                  </>
                )}
                {!simResult.success && (
                  <div className="text-[11px] text-gray-500">See the Connection Log drawer for full event history.</div>
                )}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
